import classNames from 'classnames';

import { Listbox, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import type { FieldError } from 'react-hook-form';

type Props = {
  className?: string;
  error?: FieldError;
  onChange: (newValue: number) => void;
  options: SelectOption[];
  placeholder?: string;
  value?: number;
};

export type SelectOption = {
  label: string;
  value: number;
};

/**
 * Селект рецепта. Отличается от общего `atoms/Select` компактной вёрсткой
 * (шеврон внутри потока, а не абсолютом) — это нужно, чтобы уменьшенный вариант
 * в строке приёма не ломался. Общий атом трогать нельзя: он живёт ещё
 * на регистрации, логине врача и в карточке пациента.
 */
export const Select = ({
  className,
  error,
  onChange,
  options,
  placeholder,
  value,
}: Props) => {
  const selectedOption =
    value !== undefined
      ? options.find((option) => option.value === value)
      : undefined;

  return (
    <Listbox value={value ?? undefined} onChange={onChange}>
      {({ open }) => (
        <div className={classNames('relative', className)}>
          <Listbox.Button
            className={classNames(
              'flex h-14 w-full items-center gap-3 rounded-xl border bg-white px-5 py-[18px] text-left outline-none transition hover:border-[#0788f1] focus:border-[#0788f1]',
              error ? 'border-red' : 'border-[#afc4d1]',
            )}
          >
            <span
              className={classNames(
                'block flex-1 truncate text-base font-normal leading-5 text-[#151515]',
                !selectedOption && 'text-[#151515]/50',
              )}
            >
              {selectedOption ? selectedOption.label : placeholder}
            </span>
            <svg
              className={classNames(
                'size-6 shrink-0 text-[#151515] transition-transform duration-200 ease-in-out',
                open && 'rotate-180',
              )}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
            >
              <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Listbox.Button>
          <Transition
            as={Fragment}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Listbox.Options className="absolute z-50 mt-1 max-h-[300px] w-full overflow-auto rounded-[12px] border border-[#AFC4D1] bg-white shadow-lg">
              {options.map((option) => (
                <Listbox.Option
                  key={option.value}
                  className={({ selected }) =>
                    classNames(
                      'relative cursor-pointer select-none p-2 hover:bg-[#F1F7FA]',
                      // Выбранное значение подсвечиваем, иначе в длинном
                      // списке непонятно, что именно выбрано сейчас.
                      selected && 'bg-[#d8dde2]',
                    )
                  }
                  value={option.value}
                >
                  {({ selected }) => (
                    <span
                      className={classNames(
                        'block truncate',
                        selected && 'font-bold',
                      )}
                    >
                      {option.label}
                    </span>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  );
};
